import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { exportConfig } from '../api/config';
import { api } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

export function Settings() {
  const showSnackbar = useSnackbar();
  const [exporting, setExporting] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportConfig();
      showSnackbar('Configuration exported successfully.', 'success');
    } catch (e: unknown) {
      showSnackbar(String(e), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setUploadFile(e.target.files[0]);
  };

  const handleImport = async () => {
    if (!uploadFile) return;
    try {
      setImporting(true);
      const form = new FormData();
      form.append('tarball', uploadFile);
      await api.post('/config/restore', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      showSnackbar('Configuration restored successfully.', 'success');
    } catch (e: unknown) {
      showSnackbar(String(e), 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} mb={3}>Settings</Typography>

      <Stack spacing={3}>
        {/* Export */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack>
              <Typography variant="h6">Export Configuration</Typography>
              <Typography variant="body2" color="text.secondary">
                Download a tarball containing all configuration files, permissions, and service definitions.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Export
            </Button>
          </Stack>
        </Paper>

        {/* Import */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack>
              <Typography variant="h6">Import Configuration</Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a configuration tarball to restore settings. Existing files are backed up before import.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <input
                type="file"
                accept=".tar.gz,.tgz"
                onChange={handleFileSelect}
                id="config-upload"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="config-upload"
                disabled={importing}
              >
                Choose file {uploadFile ? `— ${uploadFile.name}` : ''}
              </Button>
              <Button
                variant="contained"
                startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
                onClick={handleImport}
                disabled={!uploadFile || importing}
              >
                Import
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
